import _extends from "@babel/runtime/helpers/esm/extends";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["name", "label", "placeholder", "disabled", "readOnly", "handleErrorExternally", "externalErrorHandler", "errorText", "notSelectedErrorText", "onNetworkError", "onChange", "className", "$css", "url", "debounce", "baselineMatch", "children", "collapseError"],
    _excluded2 = ["onBlur"];
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { useResizeDetector } from 'react-resize-detector/build/withPolyfill';
import { StyledContainer } from '../../../Styled';
import { InputElementStyles, InputWrapperStyles } from '../Input/BaseInput';
import { useDebounce, useExtendedField } from '../../../../hooks';
import { TYPOGRAPHY_CLASSNAMES, COLORS, TYPOGRAPHY } from '../../../../themes';
import { flex, hoverMultiple, isKeyboard, TRANSITION_DEFAULT } from '../../../../styling';
import Label from '../../Label';
import { InputError } from '../Error';
import { DOWN, END, ENTER, ESC, HOME, IS_PROD, LEFT, RIGHT, UP } from '../../../../constants';
import { dotToDash, normalizeIndex, addInputErrorAriaProps } from '../../../../utils';
var ComboBox = /*#__PURE__*/styled(StyledContainer).withConfig({
  displayName: "TypeaheadInput__ComboBox",
  componentId: "sc-kuhq2u-0"
})(["position:relative;z-index:1;border-radius:0.4rem;"]);
var InputWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "TypeaheadInput__InputWrapper",
  componentId: "sc-kuhq2u-1"
})(["position:relative;z-index:1;", ";"], InputWrapperStyles);
var Input = /*#__PURE__*/styled.input.withConfig({
  displayName: "TypeaheadInput__Input",
  componentId: "sc-kuhq2u-2"
})(["position:relative;", ";z-index:2;background:white;"], InputElementStyles);
var ListboxWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "TypeaheadInput__ListboxWrapper",
  componentId: "sc-kuhq2u-3"
})(["position:absolute;top:calc(100% - 4px);left:-1px;right:-1px;height:", "px;overflow:hidden;pointer-events:none;"], function (_ref) {
  var $height = _ref.$height;
  return $height ? $height + 1 : 0;
});
var ListboxLine = /*#__PURE__*/styled.div.withConfig({
  displayName: "TypeaheadInput__ListboxLine",
  componentId: "sc-kuhq2u-4"
})(["position:absolute;z-index:1;top:0;left:0;right:0;", ";> div{width:calc(100% - 3.2rem);height:1px;background:", ";}"], flex('center', 'center'), COLORS.NEUTRAL_200);
var Listbox = /*#__PURE__*/styled.ul.withConfig({
  displayName: "TypeaheadInput__Listbox",
  componentId: "sc-kuhq2u-5"
})(["width:100%;border:1px solid ", ";border-top:0;border-radius:0 0 0.4rem 0.4rem;background:", ";", ";li{cursor:pointer;user-select:none;width:100%;", ";box-sizing:border-box;padding:1.6rem;", ";pointer-events:auto;", " &:nth-of-type(", "){background:", ";color:", ";}}"], COLORS.PRIMARY_600, COLORS.WHITE, function (_ref2) {
  var $showing = _ref2.$showing;
  return $showing ? css(["transform:none;transition:", ";visibility:visible;"], TRANSITION_DEFAULT) : css(["transform:translateY(-100%);visibility:hidden;"]);
}, TYPOGRAPHY.INPUT_SEMIBOLD, flex('flex-start', 'center'), hoverMultiple([{
  prop: 'color',
  base: COLORS.NEUTRAL_600,
  hover: COLORS.WHITE
}, {
  prop: 'background',
  base: 'none',
  hover: COLORS.PRIMARY_500,
  active: COLORS.PRIMARY_700
}]), function (_ref3) {
  var $activeIndex = _ref3.$activeIndex;
  return $activeIndex + 1;
}, COLORS.PRIMARY_500, COLORS.WHITE);
var DEFAULT_BASELINE_REGEX = /^.+ .+/;
var LISTBOX_INACTIVE_INDEX = 9999;
var TYPEAHEAD_OVERRIDE = 'TYPEAHEAD_OVERRIDE';

var _ref6 = /*#__PURE__*/React.createElement(ListboxLine, {
  className: "typeahead-listbox-line"
}, /*#__PURE__*/React.createElement("div", null));

var TypeaheadInput = function TypeaheadInput(_ref4) {
  var name = _ref4.name,
      label = _ref4.label,
      placeholder = _ref4.placeholder,
      disabled = _ref4.disabled,
      readOnly = _ref4.readOnly,
      handleErrorExternally = _ref4.handleErrorExternally,
      externalErrorHandler = _ref4.externalErrorHandler,
      errorText = _ref4.errorText,
      notSelectedErrorText = _ref4.notSelectedErrorText,
      onNetworkError = _ref4.onNetworkError,
      onChange = _ref4.onChange,
      className = _ref4.className,
      $css = _ref4.$css,
      url = _ref4.url,
      _ref4$debounce = _ref4.debounce,
      debounce = _ref4$debounce === void 0 ? 100 : _ref4$debounce,
      _ref4$baselineMatch = _ref4.baselineMatch,
      baselineMatch = _ref4$baselineMatch === void 0 ? DEFAULT_BASELINE_REGEX : _ref4$baselineMatch,
      children = _ref4.children,
      collapseError = _ref4.collapseError,
      rest = _objectWithoutProperties(_ref4, _excluded);

  var _useState = useState(''),
      _useState2 = _slicedToArray(_useState, 2),
      listboxError = _useState2[0],
      setListboxError = _useState2[1];

  var _useExtendedField = useExtendedField({
    name: name
  }, {
    onChange: onChange,
    errorText: listboxError || errorText
  }),
      _useExtendedField2 = _slicedToArray(_useExtendedField, 3),
      _useExtendedField2$ = _useExtendedField2[0],
      _onBlur = _useExtendedField2$.onBlur,
      formikFieldConfig = _objectWithoutProperties(_useExtendedField2$, _excluded2),
      _useExtendedField2$2 = _useExtendedField2[1],
      error = _useExtendedField2$2.errorText,
      value = _useExtendedField2$2.value,
      setValue = _useExtendedField2[2].setValue;

  var controllerRef = useRef(new AbortController());
  var preventDuplicateCallAfterReturnRef = useRef(false);
  var preventInitialCallOnExistingValueRef = useRef(false);
  var networkErrorRef = useRef(false);
  var listboxElRef = useRef();
  var listboxId = useRef("".concat(dotToDash(name), "-listbox")).current;
  var labelId = useRef("".concat(dotToDash(name), "-label")).current;
  var inputIdRef = useRef(dotToDash(name)).current;
  var debouncedValue = useDebounce(value, debounce);

  var _useResizeDetector = useResizeDetector({
    refreshMode: 'debounce',
    refreshRate: 0
  }),
      height = _useResizeDetector.height,
      resizeRef = _useResizeDetector.ref;

  var _useState3 = useState([]),
      _useState4 = _slicedToArray(_useState3, 2),
      listboxData = _useState4[0],
      setListboxData = _useState4[1];

  var _useState5 = useState(false),
      _useState6 = _slicedToArray(_useState5, 2),
      listboxShowing = _useState6[0],
      setListboxShowing = _useState6[1];

  var _useState7 = useState(LISTBOX_INACTIVE_INDEX),
      _useState8 = _slicedToArray(_useState7, 2),
      activeListboxIndex = _useState8[0],
      setActiveListboxIndex = _useState8[1];

  var listboxWasSelectedOnceRef = useRef(false);

  var _useState9 = useState(false),
      _useState10 = _slicedToArray(_useState9, 2),
      focused = _useState10[0],
      setFocused = _useState10[1];

  useEffect(function () {
    if (value) preventInitialCallOnExistingValueRef.current = true; // eslint-disable-next-line
  }, []);
  useEffect(function () {
    if (externalErrorHandler) {
      externalErrorHandler(listboxError || error || '');
    }
  }, [listboxError, error, externalErrorHandler]);
  var setRefs = useCallback(function (ref) {
    listboxElRef.current = ref;
    resizeRef.current = ref;
  }, [resizeRef]);
  useEffect(function () {
    if (preventDuplicateCallAfterReturnRef.current) {
      preventDuplicateCallAfterReturnRef.current = false;
      return;
    }

    if (preventInitialCallOnExistingValueRef.current) {
      preventInitialCallOnExistingValueRef.current = false;
      return;
    }

    if (debouncedValue !== null && debouncedValue !== void 0 && debouncedValue.match(baselineMatch)) {
      var overrideTypeaheadForTesting = !IS_PROD && debouncedValue.includes(TYPEAHEAD_OVERRIDE);

      var _value = overrideTypeaheadForTesting ? debouncedValue.replace(TYPEAHEAD_OVERRIDE, '') : debouncedValue;

      var abortableFetch = 'signal' in new Request('') ? window.fetch : fetch;
      abortableFetch("".concat(url).concat(_value), {
        signal: controllerRef.current.signal
      }).then(function (resp) {
        return resp.json();
      }).then(function (data) {
        if (data && Array.isArray(data)) {
          setListboxData(data);

          if (overrideTypeaheadForTesting) {
            setTimeout(function () {
              var listboxEl = listboxElRef.current;

              if (listboxEl && listboxEl.children[0]) {
                listboxEl.children[0].click();
              }
            }, 300);
          }
        }
      })["catch"](function (e) {
        if (e.name !== 'AbortError') {
          if (onNetworkError) {
            onNetworkError();
          }

          networkErrorRef.current = true;
        }
      });
    } // onNetworkError needs to be out of array
    // eslint-disable-next-line

  }, [debouncedValue, baselineMatch, url]);
  useEffect(function () {
    return function () {
      controllerRef.current.abort();
    };
  }, []);
  useEffect(function () {
    if (listboxData.length > 0) setListboxShowing(true);
  }, [listboxData]);
  var clearData = useCallback(function () {
    var timeout = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
    controllerRef.current.abort();
    controllerRef.current = new AbortController();
    setTimeout(function () {
      setListboxShowing(false);
    }, timeout);
  }, []);
  var onListboxItemSelect = useCallback(function () {
    preventDuplicateCallAfterReturnRef.current = true;
    listboxWasSelectedOnceRef.current = true;
    clearData();
  }, [clearData]);
  var inputKeyDown = useCallback(function (_ref5) {
    var _listboxElRef$current, _listboxElRef$current2;

    var key = _ref5.key,
        keyCode = _ref5.keyCode;
    var theKey = key || keyCode;
    var numListItems = (_listboxElRef$current = listboxElRef.current) === null || _listboxElRef$current === void 0 ? void 0 : (_listboxElRef$current2 = _listboxElRef$current.children) === null || _listboxElRef$current2 === void 0 ? void 0 : _listboxElRef$current2.length;
    var listboxItemActive = activeListboxIndex !== LISTBOX_INACTIVE_INDEX;

    if (ENTER.includes(theKey) && listboxItemActive) {
      var _listboxElRef$current3, _listboxElRef$current4;

      var activeEl = (_listboxElRef$current3 = listboxElRef.current) === null || _listboxElRef$current3 === void 0 ? void 0 : (_listboxElRef$current4 = _listboxElRef$current3.children) === null || _listboxElRef$current4 === void 0 ? void 0 : _listboxElRef$current4[activeListboxIndex];

      if (activeEl) {
        activeEl.click();
        setActiveListboxIndex(LISTBOX_INACTIVE_INDEX);
        onListboxItemSelect();
      }
    } else if (DOWN.includes(theKey)) {
      if (!numListItems) return;
      if (!listboxItemActive) setActiveListboxIndex(0);else setActiveListboxIndex(function (index) {
        return normalizeIndex(index + 1, numListItems);
      });
    } else if (UP.includes(theKey)) {
      if (!numListItems) return;
      if (!listboxItemActive) setActiveListboxIndex(numListItems - 1);else setActiveListboxIndex(function (index) {
        return normalizeIndex(index - 1, numListItems);
      });
    } else if (ESC.includes(theKey)) {
      if (!numListItems) setValue('');else {
        setActiveListboxIndex(LISTBOX_INACTIVE_INDEX);
        clearData();
      }
    } else if (RIGHT.includes(theKey) || LEFT.includes(theKey) || HOME.includes(theKey) || END.includes(theKey)) {
      setActiveListboxIndex(LISTBOX_INACTIVE_INDEX);
    }
  }, [activeListboxIndex, clearData, onListboxItemSelect, setValue]);
  return /*#__PURE__*/React.createElement(ComboBox, {
    className: className,
    $css: $css
  }, /*#__PURE__*/React.createElement(Label, {
    htmlFor: inputIdRef,
    label: label,
    placeholder: placeholder,
    id: labelId
  }), /*#__PURE__*/React.createElement(InputWrapper, {
    className: "input-wrapper",
    $disabled: disabled || readOnly,
    $error: !!error,
    $focus: focused
  }, /*#__PURE__*/React.createElement(Input, _extends({
    id: inputIdRef,
    "data-test-id": inputIdRef
  }, rest, formikFieldConfig, {
    role: "combobox",
    "aria-activedescendant": activeListboxIndex === LISTBOX_INACTIVE_INDEX ? undefined : "".concat(listboxId, "-").concat(activeListboxIndex),
    "aria-autocomplete": "list",
    "aria-controls": listboxId,
    "aria-expanded": listboxShowing ? 'true' : 'false',
    "aria-haspopup": "listbox",
    placeholder: placeholder,
    disabled: disabled,
    readOnly: readOnly,
    className: TYPOGRAPHY_CLASSNAMES.INPUT_SEMIBOLD,
    onFocus: function onFocus(e) {
      setListboxError('');
      if (listboxData.length > 0) setListboxShowing(true);
      setFocused(isKeyboard(e));
    },
    onBlur: function onBlur(e) {
      // Had to set timeout because it fires before the onClick handler in the listbox <li>
      clearData(300);

      _onBlur(e);

      setFocused(false);
      setTimeout(function () {
        if (notSelectedErrorText && !listboxWasSelectedOnceRef.current && !networkErrorRef.current) {
          setListboxError(notSelectedErrorText);
        }
      }, 300);
    },
    onKeyDown: inputKeyDown
  }, addInputErrorAriaProps(error, inputIdRef, rest))), /*#__PURE__*/React.createElement(ListboxWrapper, {
    $height: height
  }, _ref6, /*#__PURE__*/React.createElement(Listbox, {
    role: "listbox",
    "aria-label": "Search results",
    id: listboxId,
    "data-test-id": listboxId,
    ref: setRefs,
    $activeIndex: activeListboxIndex,
    $showing: listboxShowing,
    onClick: onListboxItemSelect
  }, children({
    listboxId: listboxId,
    listboxData: listboxData,
    activeListboxIndex: activeListboxIndex
  })))), !handleErrorExternally && /*#__PURE__*/React.createElement(InputError, {
    errorText: error,
    collapseError: collapseError,
    errorId: inputIdRef
  }));
};

export default TypeaheadInput;